import React from "react";
import styled from "styled-components";
import {
  Heart,
  MessageCircle,
  UserPlus,
  Share2,
  Bell,
  ShieldAlert,
} from "lucide-react";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import {
  AppLayout,
  MainArea,
  FeedColumn,
  RightSidebar,
  MobileHeader,
  MobileAvatar,
  MobileTitle,
} from "../../constants/styles";
import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser";
import { apiGet, apiPut } from "../../../api";
import { router } from "@inertiajs/react";

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  position: sticky;
  top: 0;
  z-index: 40;
`;

const HeaderTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--foreground);
`;

const MarkAllButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 9999px;
  transition: background 0.2s;

  &:hover {
    background: rgba(var(--primary-rgb), 0.1);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border);
  background: var(--card);
`;

const Tab = styled.button`
  flex: 1;
  padding: 1rem;
  background: transparent;
  border: none;
  font-size: 0.9375rem;
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;

  &:hover {
    background: var(--secondary);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${(props) => (props.$active ? "56px" : "0")};
    height: 4px;
    background: var(--primary);
    border-radius: 9999px;
    transition: width 0.2s;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
`;

const EmptyIcon = styled.div`
  margin: 0 auto 1rem;
  color: var(--muted-foreground);
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--foreground);
  margin-bottom: 0.5rem;
`;

const EmptyDescription = styled.p`
  color: var(--muted-foreground);
`;

const NotificationItem = styled.div`
  border-bottom: 1px solid var(--border);
  padding: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  background: ${(props) =>
    props.$unread ? "rgba(var(--primary-rgb), 0.05)" : "var(--card)"};

  &:hover {
    background: var(--secondary);
  }
`;

const NotificationContent = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const IconWrapper = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: var(--secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: ${(props) => props.$iconColor || "var(--primary)"};
  }
`;

const NotificationBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const NotificationInfo = styled.div``;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AuthorName = styled.span`
  font-weight: 600;
  color: var(--foreground);

  &:hover {
    text-decoration: underline;
  }
`;

const AuthorHandle = styled.span`
  font-size: 0.875rem;
  color: var(--muted-foreground);
`;

const NotificationText = styled.p`
  font-size: 0.875rem;
  color: var(--foreground);
  margin-top: 0.25rem;
`;

const NotificationPreview = styled.p`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  margin-top: 0.125rem;
  font-style: italic;
`;

const Timestamp = styled.p`
  font-size: 0.75rem;
  color: var(--muted-foreground);
  margin-top: 0.5rem;
`;

const UnreadBadge = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--primary);
  flex-shrink: 0;
  margin-top: 0.5rem;
`;

const getIcon = (type) => {
  const icons = {
    like: { icon: Heart, color: "#ef4444" },
    reply: { icon: MessageCircle, color: "var(--primary)" },
    follow: { icon: UserPlus, color: "var(--primary)" },
    repost: { icon: Share2, color: "var(--primary)" },
    thread_hidden: { icon: ShieldAlert, color: "#dc2626" },
  };
  return icons[type] || { icon: Bell, color: "var(--primary)" };
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function NotificationsPage() {
  const { currentUser } = React.use(LoggedInUserContext);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState("all");

  const userInitial =
    currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";

  React.useEffect(() => {
    apiGet("/api/v1/communities/notifications")
      .then((res) => {
        if (res.parsedResponse?.data) setNotifications(res.parsedResponse.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const markAsRead = async (notification) => {
    if (!notification.is_read) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n,
        ),
      );
      await apiPut(`/api/v1/communities/notifications/${notification.id}/read`);
    }
    if (notification.url) router.visit(notification.url);
  };

  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await apiPut("/api/v1/communities/notifications/mark_all_read");
  };

  const filteredNotifications =
    activeTab === "mentions"
      ? notifications.filter((n) => n.type === "reply")
      : notifications;

  const hasUnread = notifications.some((n) => !n.is_read);

  return (
    <AppLayout>
      <LeftSidebar
        onCreatePost={() => {}}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <MainArea>
        <FeedColumn>
          <MobileHeader>
            <MobileAvatar onClick={() => setMobileSidebarOpen(true)}>
              <span>{userInitial}</span>
            </MobileAvatar>
            <MobileTitle>Notifications</MobileTitle>
          </MobileHeader>

          <HeaderBar>
            <HeaderTitle>Notifications</HeaderTitle>
            {hasUnread && (
              <MarkAllButton onClick={markAllAsRead}>
                Mark all as read
              </MarkAllButton>
            )}
          </HeaderBar>

          <TabsContainer>
            <Tab
              $active={activeTab === "all"}
              onClick={() => setActiveTab("all")}
            >
              All
            </Tab>
            <Tab
              $active={activeTab === "mentions"}
              onClick={() => setActiveTab("mentions")}
            >
              Mentions
            </Tab>
          </TabsContainer>

          {loading ? (
            <EmptyState>
              <EmptyDescription>Loading...</EmptyDescription>
            </EmptyState>
          ) : filteredNotifications.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Bell size={48} />
              </EmptyIcon>
              <EmptyTitle>No notifications yet</EmptyTitle>
              <EmptyDescription>
                When someone likes, replies, or follows you, you{"'"}ll see it
                here.
              </EmptyDescription>
            </EmptyState>
          ) : (
            filteredNotifications.map((notification) => {
              const { icon: IconComponent, color } = getIcon(notification.type);
              const actor = notification.actor;
              return (
                <NotificationItem
                  key={notification.id}
                  $unread={!notification.is_read}
                  onClick={() => markAsRead(notification)}
                >
                  <NotificationContent>
                    <IconWrapper $iconColor={color}>
                      <IconComponent size={16} />
                    </IconWrapper>
                    <NotificationBody>
                      <NotificationHeader>
                        <NotificationInfo>
                          <AuthorInfo>
                            {actor && (
                              <>
                                <a
                                  href={`/communities/profiles/${actor.slug}`}
                                  style={{ textDecoration: "none" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <AuthorName>{actor.name}</AuthorName>
                                </a>
                                <AuthorHandle>{actor.handle}</AuthorHandle>
                              </>
                            )}
                          </AuthorInfo>
                          <NotificationText>
                            {notification.title}
                          </NotificationText>
                          {notification.content && (
                            <NotificationPreview>
                              {notification.content}
                            </NotificationPreview>
                          )}
                          <Timestamp>
                            {timeAgo(notification.created_at)}
                          </Timestamp>
                        </NotificationInfo>
                        {!notification.is_read && <UnreadBadge />}
                      </NotificationHeader>
                    </NotificationBody>
                  </NotificationContent>
                </NotificationItem>
              );
            })
          )}
        </FeedColumn>

        <RightSidebar>
          <TrendingPanel />
        </RightSidebar>
      </MainArea>
    </AppLayout>
  );
}
